import type { TFrame, TPlaylistRef } from '../models/playlist'
import { normalizePlaylistRef } from './normalizePlaylistPayload'

/**
 * Indices where rotating / random embed picks may start: bond heads and standalone frames.
 * A bonded tail (e.g. HF2 when HF1→HF2) is not a separate rotation unit.
 */
export function countEmbeddedPlaylistRotatingAnchors(frames: readonly TFrame[]): number {
  if (frames.length === 0) {
    return 0
  }
  let count = 0
  for (let i = 0; i < frames.length; i++) {
    if (i === 0 || frames[i - 1]?.playContinuousWithNext !== true) {
      count++
    }
  }
  return count
}

/**
 * When random selection draws the tail of a `playContinuousWithNext` run, map it to the run head
 * so the full bonded chain plays (matches rotating semantics where windows advance by whole bonds).
 */
function bondHeadForAnchor(frames: TFrame[], anchor: TFrame): TFrame {
  const idx = frames.findIndex(f => f.id === anchor.id)
  if (idx <= 0) {
    return anchor
  }
  let h = idx
  while (h > 0 && frames[h - 1]!.playContinuousWithNext === true) {
    h--
  }
  return frames[h]!
}

/** Frames from `anchor` through the end of its bonded chain in playlist order (inclusive). */
function expandChainFromAnchor(frames: TFrame[], anchor: TFrame): TFrame[] {
  const idx = frames.findIndex(f => f.id === anchor.id)
  if (idx === -1) {
    return [anchor]
  }
  const out: TFrame[] = []
  let j = idx
  while (j < frames.length) {
    out.push(frames[j]!)
    if (frames[j]!.playContinuousWithNext !== true) {
      break
    }
    j++
  }
  return out
}

/** Append bonded followers in playlist order after the last frame of `base` (same refs as `frames`). */
function expandBondedTailAfterSlice(frames: TFrame[], base: TFrame[]): TFrame[] {
  if (base.length === 0) {
    return []
  }
  const out = [...base]
  let lastIdx = frames.findIndex(f => f.id === out[out.length - 1]!.id)
  const guard = frames.length
  let steps = 0
  while (
    lastIdx >= 0
    && lastIdx < frames.length - 1
    && frames[lastIdx]!.playContinuousWithNext === true
    && steps < guard
  ) {
    steps++
    lastIdx++
    out.push(frames[lastIdx]!)
  }
  return out
}

/** Frame index of the Nth rotating anchor (bond head or standalone frame). */
function frameIndexForRotatingAnchor(frames: readonly TFrame[], anchorIndex: number): number {
  if (frames.length === 0) {
    return 0
  }
  let anchors = 0
  for (let i = 0; i < frames.length; i++) {
    if (i === 0 || frames[i - 1]?.playContinuousWithNext !== true) {
      if (anchors === anchorIndex) {
        return i
      }
      anchors++
    }
  }
  return 0
}

function portalBookHouseEmbedStateKey(frameId: string): string {
  return `${frameId}\0portalBook`
}

/** Next rotating cursor: never land on a bonded tail index. */
function nextRotatingEmbedOffset(frames: readonly TFrame[], afterIndex: number): number {
  const L = frames.length
  if (L === 0) {
    return 0
  }
  let next = (afterIndex + 1) % L
  while (next > 0 && frames[next - 1]?.playContinuousWithNext === true) {
    next = (next + 1) % L
  }
  return next
}

/** Fisher-Yates shuffle (in-place, returns the array). */
function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i]!, arr[j]!] = [arr[j]!, arr[i]!]
  }
  return arr
}

// Module-level state persisted between queue rebuilds (keyed by frame.id).
const randomDecks = new Map<string, TFrame[]>()
const rotatingOffsets = new Map<string, number>()

/** Deep enough for peek/restore: rotating is numbers; random decks are mutated in place (shift). */
export interface EmbeddedSelectionStateSnapshot {
  rotatingOffsets: Map<string, number>
  randomDecks: Map<string, TFrame[]>
}

export function snapshotEmbeddedSelectionState(): EmbeddedSelectionStateSnapshot {
  return {
    rotatingOffsets: new Map(rotatingOffsets),
    randomDecks: new Map([...randomDecks.entries()].map(([k, v]) => [k, [...v]]))
  }
}

export function restoreEmbeddedSelectionState(snapshot: EmbeddedSelectionStateSnapshot): void {
  rotatingOffsets.clear()
  randomDecks.clear()
  for (const [k, v] of snapshot.rotatingOffsets) {
    rotatingOffsets.set(k, v)
  }
  for (const [k, v] of snapshot.randomDecks) {
    randomDecks.set(k, [...v])
  }
}

export interface SelectEmbeddedFramesOptions {
  /**
   * Lucimark Book interleaved house row: pick visit `n` from anchor `(base + n) % anchorCount`
   * without advancing the stored cursor until {@link commitPortalBookHouseRotatingAdvances}.
   */
  portalBookHouseVisitIndex?: number
  /**
   * When true, advance the rotating cursor for this embed (full loop wrap only).
   * Benign queue rebuilds must leave this false so the on-air pick stays stable.
   */
  commitRotatingEmbedAdvance?: boolean
}

/** Advance portal book house anchor cursor after a full interleaved resolve pass. */
export function commitPortalBookHouseRotatingAdvances(
  frameId: string,
  visitCount: number,
  anchorCount: number
): void {
  if (visitCount <= 0 || anchorCount <= 0) {
    return
  }
  const key = portalBookHouseEmbedStateKey(frameId)
  const base = rotatingOffsets.get(key) ?? 0
  rotatingOffsets.set(key, (base + visitCount) % anchorCount)
}

/**
 * Selects frames from an embedded playlist according to the given playlistRef config.
 * Consecutive frames linked with `playContinuousWithNext` are always returned together
 * (random: each deck draw is promoted to the bond head when needed, then expands forward; rotating: tail appended after the base slice).
 *
 * @param frames  Active frames from the embedded playlist (already date/time filtered).
 * @param ref     The playlistRef config from the embedding frame (`offset` is ignored; rotating always starts at 0).
 * @param frameId Stable ID of the embedding frame (used as state key for random/rotating).
 */
export function selectEmbeddedFrames(
  frames: TFrame[],
  ref: TPlaylistRef,
  frameId: string,
  options?: SelectEmbeddedFramesOptions
): TFrame[] {
  if (frames.length === 0) { return [] }

  const resolved = normalizePlaylistRef(ref) ?? ref
  const { frameCount } = resolved
  const rawMode = resolved.selectionMode as 'random' | 'rotating' | 'sequential'
  const mode = rawMode === 'sequential' ? 'rotating' : rawMode
  const n = frameCount ?? frames.length

  if (mode === 'random') {
    const count = Math.min(n, frames.length)
    let deck = randomDecks.get(frameId)
    if (!deck || deck.length === 0) {
      deck = shuffle([...frames])
      randomDecks.set(frameId, deck)
    }
    const selected: TFrame[] = []
    const seen = new Set<string>()
    let anchorsDrawn = 0
    while (anchorsDrawn < count) {
      if (deck.length === 0) {
        const pool = frames.filter(f => !seen.has(f.id))
        deck = pool.length > 0 ? shuffle([...pool]) : shuffle([...frames])
      }
      const rawAnchor = deck.shift()!
      const anchor = bondHeadForAnchor(frames, rawAnchor)
      const chain = expandChainFromAnchor(frames, anchor)
      deck = deck.filter(f => !chain.some(c => c.id === f.id))
      for (const f of chain) {
        if (seen.has(f.id)) {
          continue
        }
        seen.add(f.id)
        selected.push(f)
      }
      anchorsDrawn++
      randomDecks.set(frameId, deck)
    }
    randomDecks.set(frameId, deck)
    return selected
  }

  // rotating: each visit plays one anchor unit (full bonded chain when the anchor heads a link run)
  if (mode === 'rotating') {
    const count = Math.min(n, frames.length)
    const L = frames.length
    const portalBookVisit = options?.portalBookHouseVisitIndex
    let storedOffset = portalBookVisit != null
      ? rotatingOffsets.get(portalBookHouseEmbedStateKey(frameId)) ?? 0
      : rotatingOffsets.get(frameId) ?? 0
    if (portalBookVisit == null && options?.commitRotatingEmbedAdvance === true && L > 0) {
      storedOffset = nextRotatingEmbedOffset(frames, storedOffset % L)
      rotatingOffsets.set(frameId, storedOffset)
    }
    const currentOffset = portalBookVisit != null
      ? frameIndexForRotatingAnchor(
          frames,
          (() => {
            const anchorCount = countEmbeddedPlaylistRotatingAnchors(frames)
            if (anchorCount <= 0) {
              return 0
            }
            const anchorBase = rotatingOffsets.get(portalBookHouseEmbedStateKey(frameId)) ?? 0
            return (anchorBase + portalBookVisit) % anchorCount
          })()
        )
      : storedOffset % L
    const rawStart = portalBookVisit != null ? currentOffset : currentOffset % L
    const headFrame = bondHeadForAnchor(frames, frames[rawStart]!)
    const headIdx = frames.findIndex(f => f.id === headFrame.id)
    const start = headIdx >= 0 ? headIdx : rawStart
    const chainFromHead = expandChainFromAnchor(frames, frames[start]!)
    const playsFullPoolPerVisit = n >= frames.length
    const selected = portalBookVisit != null && !playsFullPoolPerVisit
      ? chainFromHead
      : chainFromHead.length > 1
        ? chainFromHead
        : expandBondedTailAfterSlice(
            frames,
            [...frames, ...frames].slice(start, start + count)
          )
    return selected
  }

  return []
}

/** Clears random embed decks only (e.g. after a full playback loop). Rotating offsets are preserved. */
export function resetEmbeddedRandomDeckState(): void {
  randomDecks.clear()
}

/** Resets all persisted selection state (e.g. when campaign changes). */
export function resetEmbeddedFrameSelectionState(): void {
  resetEmbeddedRandomDeckState()
  rotatingOffsets.clear()
}
